/**
 * UranIA PA — `/manutenzione` (fermo programmato).
 *
 * "Con finestra e riferimento" (Riordino della mappa delle rotte §3):
 * nessuna finestra reale è nota qui — non inventiamo date. La pagina
 * legge `?finestra=` e `?riferimento=` dalla query string, così chi
 * annuncia il fermo (runbook ops, non questa SPA) può linkare una
 * finestra e un riferimento specifici; senza query string mostra solo il
 * messaggio generico. Component + `Route` nello stesso file (non in
 * `features/`) perché usa `Route.useSearch()` — stesso pattern di
 * `console.tsx` e `login/staging.tsx`.
 *
 * Link "torna alla copertina" con `<a href="/">` semplice, non `<Link>`:
 * una pagina di manutenzione deve restare raggiungibile/uscibile anche
 * se il router SPA stesso è nello stato che ha causato il fermo.
 */

import { useTranslation } from 'react-i18next';
import { createRoute } from '@tanstack/react-router';
import type { JSX } from 'react';

import { Route as RootRoute } from './__root.tsx';

function ManutenzionePage(): JSX.Element {
  const { t } = useTranslation();
  const { finestra, riferimento } = Route.useSearch();

  return (
    <section
      className="container py-5"
      data-testid="manutenzione-page"
      aria-labelledby="manutenzione-heading"
    >
      <div className="mx-auto text-center" style={{ maxWidth: 560 }}>
        <span
          className="badge mb-3"
          style={{
            backgroundColor: '#fff3cd',
            color: '#664d03',
            fontSize: '0.7rem',
            letterSpacing: '0.05em',
          }}
        >
          {t('sistema.manutenzione.title')}
        </span>
        <h1 id="manutenzione-heading" className="h3 mb-2">
          {t('sistema.manutenzione.title')}
        </h1>
        <p className="text-muted mb-3">{t('sistema.manutenzione.body')}</p>
        {finestra.length > 0 && (
          <p className="small mb-1" data-testid="manutenzione-finestra">
            {t('sistema.manutenzione.window_label', { window: finestra })}
          </p>
        )}
        {riferimento.length > 0 && (
          <p className="small text-muted mb-4" data-testid="manutenzione-riferimento">
            {t('sistema.manutenzione.reference_label', { reference: riferimento })}
          </p>
        )}
        <a href="/" className="btn btn-outline-primary" data-testid="manutenzione-back">
          {t('sistema.manutenzione.back_to_landing')}
        </a>
      </div>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/manutenzione',
  validateSearch: (search: Record<string, unknown>): { finestra: string; riferimento: string } => ({
    finestra: typeof search.finestra === 'string' ? search.finestra : '',
    riferimento: typeof search.riferimento === 'string' ? search.riferimento : '',
  }),
  component: ManutenzionePage,
});
